'use client'

import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { PageLayout } from '@/ui/layouts/PageLayout'
import { InfoIcon } from 'lucide-react'
import { useSearchParams } from 'next/navigation'
import { useCallback, useRef } from 'react'
import { useAccount } from 'wagmi'
import {
  CardCTA,
  CardFAQ,
  CardFeatures,
  CardHero,
  CardHowItWorks,
  CardLandingEligibility,
  CardStats,
} from '../components/landing'
import { useCardAuth } from '../hooks/useCardAuth'
import { useCardEligibility } from '../hooks/useCardEligibility'
import { CardEligibilityResult } from '../types/CardState'

interface CardLandingPageProps {
  onEligibilityChecked: (result: CardEligibilityResult) => void
}

export function CardLandingPage({ onEligibilityChecked }: CardLandingPageProps) {
  const { address } = useAccount()
  const { hasTokens } = useCardAuth()
  const { checkEligibility, isLoading } = useCardEligibility()
  const searchParams = useSearchParams()
  const referralCode = searchParams.get('ref')?.trim() || undefined
  const eligibilityRef = useRef<HTMLDivElement>(null)
  const isReturningUser = !!address && hasTokens
  const isCheckingRef = useRef(false)

  const scrollToEligibility = useCallback(() => {
    eligibilityRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])

  const isBusy = isLoading

  const handleHeroAction = useCallback(async () => {
    if (!isReturningUser) {
      scrollToEligibility()
      return
    }

    if (isCheckingRef.current) return
    isCheckingRef.current = true

    try {
      const result = await checkEligibility(referralCode)
      onEligibilityChecked(result)
    } catch (err) {
      console.error('Error during sign-in:', err)
    } finally {
      isCheckingRef.current = false
    }
  }, [isReturningUser, checkEligibility, referralCode, onEligibilityChecked, scrollToEligibility])

  return (
    <PageLayout className="max-w-6xl gap-0">
      <CardHero
        onGetStarted={handleHeroAction}
        isReturningUser={isReturningUser}
        walletAddress={address}
        isSigningIn={isBusy}
      />
      <CardStats />
      <CardFeatures />
      <CardHowItWorks />
      <CardLandingEligibility ref={eligibilityRef} onEligibilityChecked={onEligibilityChecked} />
      <CardFAQ />
      <CardCTA onGetStarted={handleHeroAction} />

      <div className="mt-8 pb-8 text-center">
        <Panel.Wrapper className="p-4">
          <Panel.Content className="flex flex-row items-center justify-center gap-2">
            <InfoIcon className="h-4 w-4 flex-shrink-0 text-dim-foreground" />
            <Typography variant="p" className="text-center text-dim-foreground text-xs">
              HypurrFi Card is not affiliated with the HypurrFi Protocol and subject to separate terms provided by
              Issuer.
            </Typography>
          </Panel.Content>
        </Panel.Wrapper>
      </div>
    </PageLayout>
  )
}
